import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createImageUploadUrl } from '../../services/apiService';
import styles from './ImageUpload.module.css';

// Helper function for client-side image resizing
const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          file.type,
          0.9
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

function ImageUpload({ onUploadSuccess }) {
  const { getAccessTokenSilently } = useAuth0();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    const localPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(localPreviewUrl);

    try {
      const resizedFileBlob = await resizeImage(file, 400, 400);
      const accessToken = await getAccessTokenSilently();
      const { uploadUrl, finalImageUrl } = await createImageUploadUrl(
        { fileType: resizedFileBlob.type },
        accessToken
      );

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': resizedFileBlob.type },
        body: resizedFileBlob,
      });

      onUploadSuccess({
        finalImageUrl: finalImageUrl,
        previewUrl: localPreviewUrl,
      });
    } catch (err) {
      setError('Upload failed. Please try again.');
      setPreviewUrl(null);
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.preview}>
        {previewUrl ? (
          <img src={previewUrl} alt="New persona avatar preview" />
        ) : (
          <div className={styles.placeholder}>
            <i className="fas fa-camera"></i>
            <span>New Image</span>
          </div>
        )}
      </div>
      <div className={styles.controls}>
        <input
          type="file"
          id="imageUploadInput"
          accept="image/png, image/jpeg, image/gif"
          onChange={handleFileChange}
          disabled={isUploading}
          className={styles.fileInput}
        />
        <label
          htmlFor="imageUploadInput"
          className={`action-button secondary ${isUploading ? styles.disabled : ''}`}
        >
          {isUploading ? 'Uploading...' : 'Choose File'}
        </label>
        {error && <p className="errorText">{error}</p>}
      </div>
    </div>
  );
}

export default ImageUpload;
